import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Wallet, TrendingDown, FileSignature, ShieldX, Wrench } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { api } from "../../lib/api";

const PIE = ["#ff0033", "#ff8aa3", "#f59e0b", "#3b82f6", "#10b981", "#a855f7", "#737373"];

const Big = ({ icon: Icon, label, value, accent, color }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    className={`glass rounded-2xl p-5 ${accent ? "ring-1 ring-[#ff0033]/30" : ""}`}>
    <div className="flex items-center justify-between mb-3">
      <Icon size={16} className={color || "text-white/60"} />
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
    </div>
    <div className="font-display text-3xl font-extrabold tracking-tight">{value}</div>
  </motion.div>
);

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/overview").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-white/40">Caricamento report…</div>;
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight">Report & Analytics</h1>
        <p className="text-sm text-white/45 mt-1">Panoramica P&L · 12 mesi · spese · top revenue · danni · sinistri</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Big icon={TrendingUp} label="Entrate Totali" value={`€${t.revenue.toLocaleString("it-IT")}`} accent color="text-[#ff0033]" />
        <Big icon={Wallet} label="Spese Totali" value={`€${t.expenses.toLocaleString("it-IT")}`} color="text-amber-300" />
        <Big icon={TrendingDown} label="Profitto Netto" value={`€${t.profit.toLocaleString("it-IT")}`} color={t.profit >= 0 ? "text-emerald-300" : "text-[#ff0033]"} />
        <Big icon={FileSignature} label="Contratti Attivi" value={`${t.contratti_attivi}/${t.contratti_totali}`} color="text-blue-300" />
        <Big icon={Wrench} label="Danni Totali" value={`€${t.damages_total.toLocaleString("it-IT")}`} color="text-amber-300" />
        <Big icon={Wrench} label="Danni Addebitati" value={`€${t.damages_addebitati.toLocaleString("it-IT")}`} color="text-emerald-300" />
        <Big icon={ShieldX} label="Sinistri Aperti" value={t.sinistri_aperti} color="text-[#ff5577]" />
        <Big icon={ShieldX} label="Liquidato Assicur." value={`€${t.sinistri_liquidato.toLocaleString("it-IT")}`} color="text-emerald-300" />
      </div>

      {/* 12-month revenue */}
      <div className="glass rounded-2xl p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">Entrate</div>
        <div className="font-display text-xl font-bold mb-4">Ultimi 12 mesi</div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data.revenue_12m}>
            <defs>
              <linearGradient id="rev12" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff0033" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#ff0033" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1c1c1f" vertical={false} />
            <XAxis dataKey="mese" stroke="#666" fontSize={11} />
            <YAxis stroke="#666" fontSize={11} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #ff0033", borderRadius: 8 }} />
            <Area type="monotone" dataKey="importo" stroke="#ff0033" strokeWidth={2} fill="url(#rev12)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">Spese per Categoria</div>
          <div className="font-display text-xl font-bold mb-4">Distribuzione</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.expenses_cat} dataKey="importo" nameKey="categoria" outerRadius={90} innerRadius={50} paddingAngle={3}>
                {data.expenses_cat.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} stroke="transparent" />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #ff0033", borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#999" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">Top Auto per Revenue</div>
          <div className="font-display text-xl font-bold mb-4">Più redditizie</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.top_revenue} layout="vertical">
              <CartesianGrid stroke="#1c1c1f" horizontal={false} />
              <XAxis type="number" stroke="#666" fontSize={11} />
              <YAxis type="category" dataKey="auto" stroke="#666" fontSize={11} width={140} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #ff0033", borderRadius: 8 }} />
              <Bar dataKey="revenue" fill="#ff0033" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
